import { computed, inject, Injectable, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Id } from '@turf/turf';
import { liveQuery } from 'dexie';
import mergeLodash from 'lodash-es/merge';
import { combineLatest, from, merge, Subject } from 'rxjs';
import { filter, map, pairwise, share, startWith, takeUntil } from 'rxjs/operators';
import { Attribute, AttributeContainer, CoyoteWS } from '../class/Attribute';
import { DEVICE_STATUS, Device as LegacyDevice, Device as OldDevice } from '../class/Device';
import { Device, Location, Unit } from '../database/models';
import SocketLocation from '../database/models/SocketLocation';
import { DateHelper } from '../utils/DateHelper';
import { DeviceStateService, FullDevice } from './device-state.service';
import { DevicesService } from './devices.service';
import { HelperService } from './helper.service';
import { LocationsService } from './locations.service';
import { PreferenceService } from './preference.service';
import SocketLocationsService from './socket-locations.service';
import UnitsService from './units.service';

export type { FullDevice };

export type Beacon = FullDevice & { count_beacons: number; ids_beacons: number[] };

export type DeviceType = 'beacon' | 'device';
export interface DevicesFilters {
  movingStatus: (keyof typeof DEVICE_STATUS)[];
  units: number[];
  type: DeviceType[];
}
export type ImmobileDevice = Device & { timeOfInactivity: number };

export const BIG_PARK_POLLING_DURATION = 20 * 60 * 1000;
export const CASUAL_PARK_POLLING_DURATION = 20 * 1000;

// filter by hardware

@Injectable({
  providedIn: 'root',
})
export class DevicesManagerService {
  private _deviceStateService = inject(DeviceStateService);
  /**
   * Read entry point for the devices map: components and services read it from here. It is the
   * very same signal as DeviceStateService.$devicesMap, which is reserved for writes and for the
   * services that cannot inject this one without closing a circular-DI loop (see DeviceStateService).
   */
  public $devicesMap = this._deviceStateService.$devicesMap;
  // filter(), not skip(1): skip(1) drops whatever the map holds at subscribe time, which was safe
  // back when this piped the BehaviorSubject directly (the skipped value was its null seed). Through
  // toObservable the first emission comes from an effect running a tick later, so an already-loaded
  // map gets discarded and subscribers wait for the *next* device change instead — up to 20s away
  // wherever startThenDelay throttles them (see loadData()). The null seed is what must be dropped.
  public devices$ = toObservable(this.$devicesMap).pipe(
    filter(devicesMap => !!devicesMap),
    map(devicesMap => Object.values(devicesMap)),
    share()
  );
  public $devices = this._deviceStateService.$devices;
  public $beacons = computed(() => {
    const beacons = Object.values(this.$devicesMap() || {}).filter(device => device.hasBeacon);
    return this._groupByCoordinates(beacons);
  });
  public beacons$ = toObservable(this.$beacons);
  readonly $beaconMap = computed(() => new Map(this.$beacons().map(beacon => [beacon.uid, beacon])));
  // public $devices = toSignal(this.devices$);
  public $filters = signal<DevicesFilters>({
    movingStatus: [],
    units: [],
    type: [],
  });
  public filters$ = toObservable(this.$filters);

  /**
   * Status filter requested by another screen (the dashboard "Park state" widget) and waiting
   * to be applied the next time the park is displayed.
   *
   * Deliberately kept out of the preferences and out of the URL: this is a one-off navigation
   * intent, not a setting to keep. Consumed once, through `consumePendingStatusFilter`.
   */
  private _$pendingStatusFilter = signal<keyof typeof DEVICE_STATUS | null>(null);

  private _units$ = from(liveQuery(async () => this._unitsService.table.orderBy('tag').toArray()));
  private _$units = toSignal(this._units$);
  public $additionalDevices = signal(new Map<number, Location[]>());

  public $devicesWithWarning = computed(() => {
    const devices = this.$devices();
    if (!devices.length) return [];
    const devicesWarn = devices.filter(
      device => device.anomalyError && !device.hasPrivacy() && device.latitude !== undefined
    );
    return devicesWarn ?? [];
  });

  public $privateDevices = computed(() => {
    const devices = this.$devices();
    if (!devices.length) return [];
    const privateDevices = devices
      .filter(device => device.hasPrivacy() && device.location_date)
      .sort((a: FullDevice, b: FullDevice) => {
        return a.location_date > b.location_date ? -1 : 1;
      });
    return privateDevices ?? [];
  });

  public $locked = computed(() => {
    const devices = this.$devices();
    if (!devices.length) return [];
    const locked = devices.filter(device => device.isLocked);
    return locked ?? [];
  });

  private _socketLocationsService = inject(SocketLocationsService);
  private _locationsService = inject(LocationsService);
  private _devicesService = inject(DevicesService);
  private _preferenceService = inject(PreferenceService);
  private _helperService = inject(HelperService);
  private _unitsService = inject(UnitsService);

  private destroy$ = new Subject<void>();

  public init() {
    this._setupAggregation();
    this._setupFilters();
  }

  public clear() {
    this.destroy$.next();
    this.destroy$.complete();

    // A completed Subject can never emit/be reused: without recreating it here, the next
    // init() -> _setupAggregation() call's takeUntil(this.destroy$) would see an already-completed
    // notifier and never even subscribe to its source, so _devicesMap$ (and therefore $devices/
    // devices$, consumed by flatToTree) would silently stop updating after an account switch.
    this.destroy$ = new Subject<void>();

    // The filters mirror the preferences of the account being left, and _setupFilters() only
    // rewrites them once the *next* account's preferences land. Left alone, the map layers keep
    // filtering the new fleet with the previous account's status/type, and the "active filters"
    // indicator of the dashboard lights up on an account that has none.
    this.$filters.set({ movingStatus: [], units: [], type: [] });
    this._deviceStateService.clear();
  }

  // CRUD //
  public get(uid: number | Id): FullDevice {
    return this.$devicesMap()?.[uid];
  }

  public get$(uid: number) {
    const fromLocation$ = this._locationsService.getById$(uid).pipe(
      filter(location => !!location),
      map(() => this.get(uid))
    );

    const fromSocket$ = this._socketLocationsService.changedValues$.pipe(
      filter(socketLocations => !!socketLocations?.[uid]),
      map(() => this.get(uid))
    );

    return merge(fromLocation$, fromSocket$);
  }

  public getRegistration(device: Location) {
    switch (this._preferenceService.$preference()?.config?.deviceView) {
      case 'snexternal':
        return device.snexternal ? device.snexternal : device.registration;
      case 'vin':
        return device.vin ? device.vin.toString() : device.registration;
      case 'summary':
        return device.summary ? device.summary : device.registration;
      case 'registration':
      default:
        return device.registration;
    }
  }

  private _setupFilters() {
    this._preferenceService.preferencesLoaded$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      const unitsIdsFromPreferences = this._preferenceService.getUnitsFilter();
      const movingStatusFilterPreferences = this._preferenceService.getMovingStatusFilter() || [];
      const typeFilterPreferences = this._preferenceService.getDeviceTypeFilter() || [];

      this.$filters.set({
        movingStatus: movingStatusFilterPreferences,
        units: unitsIdsFromPreferences,
        type: typeFilterPreferences,
      });
    });
  }

  public setFilters({
    filters,
    currentUnitsIds = [],
  }: {
    filters: Partial<{
      typeFilter: DeviceType[];
      statusFilter: (keyof typeof DEVICE_STATUS)[];
      unitsFilter: Unit[];
    }>;
    currentUnitsIds?: number[];
  }) {
    const unitsId = filters.unitsFilter.map(({ uid }) => uid);
    //we need to check if there is an intersection between the current units and the units filtered
    this.$filters.set({
      type: filters.typeFilter,
      movingStatus: filters.statusFilter,
      units: this.unitsToHide(
        currentUnitsIds.length &&
          (currentUnitsIds.some(unitId => unitsId.includes(unitId)) || unitsId.length === 0)
          ? currentUnitsIds
          : unitsId
      ),
    });
  }

  /** Arms the status filter to apply the next time the park is displayed. */
  public setPendingStatusFilter(movingStatus: keyof typeof DEVICE_STATUS) {
    this._$pendingStatusFilter.set(movingStatus);
  }

  /**
   * Returns the pending status filter and disarms it, so that coming back to the park later
   * does not re-apply it while the user may have removed it in the meantime.
   */
  public consumePendingStatusFilter(): keyof typeof DEVICE_STATUS | null {
    const pendingStatusFilter = this._$pendingStatusFilter();
    this._$pendingStatusFilter.set(null);
    return pendingStatusFilter;
  }

  public unitsToHide(unitsId: number[]) {
    if (!unitsId.length) return [];

    return this._$units()
      .map(({ uid }) => uid)
      .filter(uid => !unitsId.includes(uid));
  }

  private _setupAggregation() {
    combineLatest([this._locationsService.values$, this._devicesService.values$])
      .pipe(takeUntil(this.destroy$), startWith([[], []]), pairwise())
      .subscribe(([prev, current]) => {
        const [prevLocationsDevices, prevDevices] = prev;
        const [locationsDevices, devices] = current;

        const locationsDevicesChanged =
          JSON.stringify(prevLocationsDevices) !== JSON.stringify(locationsDevices);
        const devicesChanged = JSON.stringify(prevDevices) !== JSON.stringify(devices);

        if (!locationsDevices) return;

        // Must be a fresh object reference, not the mutated current value: the backing signal's
        // set() does an Object.is identity check, so re-publishing the SAME (mutated) reference is
        // silently dropped for every consumer of $devicesMap — and for devices$ too, since it is
        // derived from that signal through toObservable(). Only a genuine reference change (as this
        // shallow copy guarantees on every call) propagates the update.
        const currentDevices = { ...(this.$devicesMap() || {}) };
        // Process locations devices
        if (locationsDevicesChanged) {
          const mainLocationsDevices = this.getMainDevices(locationsDevices, { setAdditionalDevices: true });

          mainLocationsDevices.forEach(device => {
            device.normalize();

            const existingDevice = currentDevices?.[device.uid];
            const registration = this.getRegistration(device);
            const updatedDevice = {
              ...(existingDevice || {}),
              ...device,
              registration,
            };

            currentDevices[device.uid] = new OldDevice(updatedDevice) as FullDevice;
          });
        }

        // Process standard devices
        if (devicesChanged) {
          const mainDevices = this.getMainDevices(locationsDevices);

          mainDevices.forEach(device => {
            const updatedDevice = { ...(currentDevices?.[device.uid] || {}), ...device };
            currentDevices[device.uid] = new OldDevice(updatedDevice) as FullDevice; //TODO find better type
          });
        }

        if (locationsDevices || devicesChanged) this._deviceStateService.setDevices(currentDevices);
      });

    // the update here is separated from the others because process only changed devices from socket.
    this._socketLocationsService.changedValues$.pipe(takeUntil(this.destroy$)).subscribe(changedDevices => {
      if (!changedDevices) {
        return;
      }

      if (changedDevices) {
        const mainDevices = this.getMainDevicesFromSocket(changedDevices);
        this._deviceStateService.setDevices({
          ...this.$devicesMap(),
          ...mainDevices,
        } as any); //TODO type
      }
      const currentDevices = this.$devicesMap();
      const updatedDevices: Record<number, FullDevice> = {};
      if (!currentDevices || !Object.keys(currentDevices).length) {
        return;
      }
      Object.entries(changedDevices).forEach(([uid, incomingDevice]) => {
        const existingDevice = currentDevices[Number(uid)];
        if (existingDevice) {
          const mergedDevice = mergeLodash(this._helperService.cloneDeep(existingDevice), incomingDevice);
          updatedDevices[Number(uid)] = new OldDevice(mergedDevice) as FullDevice;
        } else {
          const newDevice = new OldDevice(incomingDevice);
          updatedDevices[newDevice.uid] = newDevice as FullDevice;
        }
      });

      this._deviceStateService.setDevices({
        ...currentDevices,
        ...updatedDevices,
      });
    });
  }

  public static setIconDevice(args: Location | SocketLocation | LegacyDevice) {
    if (args.newStatus !== 3 || (args.special_flag & 2) !== 0) {
      let color = 0;

      if ((args.special_flag & 2) === 0) {
        const map = { 0: 0, 1: 3, 2: 2 };
        color = map[args.newStatus] ?? 0;
      }
      args.icon = args.id_symbol > 0 ? args.id_symbol * 4 + color : 0 + color;
      args.deviceStatusClass = `device_${args.icon + 1} status_img`;
    } else {
      let direction = 0;

      if (args.course < 15 || args.course >= 345) {
        direction = 0;
      } else if (args.course < 45) {
        direction = 1;
      } else if (args.course < 75) {
        direction = 2;
      } else if (args.course < 105) {
        direction = 3;
      } else if (args.course < 135) {
        direction = 4;
      } else if (args.course < 165) {
        direction = 5;
      } else if (args.course < 195) {
        direction = 6;
      } else if (args.course < 225) {
        direction = 7;
      } else if (args.course < 255) {
        direction = 8;
      } else if (args.course < 285) {
        direction = 9;
      } else if (args.course < 315) {
        direction = 10;
      } else if (args.course < 345) {
        direction = 11;
      }
      args.icon = 160 + (args.id_symbol > 0 ? args.id_symbol * 12 : 0) + direction;
      args.deviceStatusClass = `device_${args.icon + 1} status_img`;
    }
  }

  public static setStatusDevice(args: Location | SocketLocation | LegacyDevice, realTime?: number) {
    const now = realTime ? realTime : new Date().getTime() / 1000;
    const nowMinus5Minutes = now - 300;
    const nowMinus40Minutes = now - 2400;

    // DEFAULT // INACTIVE // GRAY
    args.newStatus = 0;
    args.movingStatus = DEVICE_STATUS.inactive;

    // if not private device + have coords + dev_hw_id => ACTIVE
    // else default => INACTIVE
    if (
      (args.special_flag & 2) === 0 &&
      // TODO : When we know if device is virtual, can activate this additionnal condition
      // args?.dev_hw_id &&
      args?.longitude &&
      args?.latitude &&
      args.longitude !== 0 &&
      args.latitude !== 0
    ) {
      if (((args.input & 2) == 0 && args.speed_current <= 3) || args.location_date < nowMinus40Minutes) {
        //current speed || 40min
        // if(status != 1  && (args.speed_current >= 3) && (args.location_date > (now - 6000))) console.log('now', now,this)
        args.newStatus = 1; //IDLE //RED
        args.movingStatus = DEVICE_STATUS.fullyStopped;
      } else if (args.speed_max <= 3 || args.location_date < nowMinus5Minutes) {
        //speed || 5min
        args.newStatus = 2; //NOT MOVING //ORANGE
        args.movingStatus = DEVICE_STATUS.stopped;
      } else {
        args.newStatus = 3; //MOVING //GREEN
        args.movingStatus = DEVICE_STATUS.moving;
      }
    }
    this.setIconDevice(args);
  }

  public static setHDOP(item: Location | SocketLocation) {
    item.hdop = item.hdop > 32768 ? item.hdop * 10 : item.hdop;

    item.isBlurHdop = item.hdop > 100;
  }

  public static setBatteryLevel(item: Location) {
    switch (item.soh) {
      case '1_OK':
        item.batteryLevelIcon = 'signal_cellular_alt';
        break;
      case '2_Warning':
        item.batteryLevelIcon = 'signal_cellular_alt_2_bar';
        break;
      case '3_BigWarning':
        item.batteryLevelIcon = 'signal_cellular_alt_1_bar';
        break;
      case '4_KO':
        item.batteryLevelIcon = 'signal_cellular_alt_1_bar';
        break;
      default:
        item.batteryLevelIcon = item.soh;
        break;
    }
  }

  public static setStatusLock(args: Location | LegacyDevice) {
    if (
      args.locking_request_status === 'S' ||
      (!args.locking_request_status && !args.locking_request_successed_type && args.locking_request_type)
    ) {
      args.status_lock = args.locking_request_type;
    } else if (args.locking_request_status === 'C') {
      args.status_lock = args.locking_request_successed_type ? args.locking_request_successed_type : 'U';
    } else if (!args.locking_request_status && args.locking_request_successed_type) {
      args.status_lock = args.locking_request_successed_type === 'L' ? 'U' : 'L';
    } else if (
      !args.locking_request_status &&
      !args.locking_request_successed_type &&
      !args.locking_request_type
    ) {
      args.status_lock = 'U';
    }
    args.isLocked = args.status_lock === 'L';
    args.isWaitingCommand =
      args.locking_request_status !== 'S' &&
      args.locking_request_status !== 'C' &&
      !!args.locking_request_type;
  }

  /**
   * Get if there is an error on GPS or transmission of a device based on the number of
   * satellite used for GPS (gps_sat_used) and the transmission status (expired or not).
   * @returns - message error if there is one.
   */
  public static getAnomalyError(args: Location | LegacyDevice) {
    // check if last transmission is older than 8days
    const isLastTransmissionExpired7Days = args.device_log <= new DateHelper().subtractDays(8).timestamp();
    if (args.hasBeacon) {
      return isLastTransmissionExpired7Days ? 'ANOMALY_BEACON' : null;
    }

    // check if last transmission is older than 25h
    const isLastTransmissionExpired = args.device_log <= new DateHelper().subtractHours(25).timestamp();

    if (args.gps_sat_used < 3 && isLastTransmissionExpired) {
      return 'GPS_TRANS_ANOMALY';
    } else if (args.gps_sat_used < 3 && !isLastTransmissionExpired) {
      return 'GPS_ANOMALY';
    } else if (args.gps_sat_used >= 3 && isLastTransmissionExpired) {
      return 'TRANSMISSION_ANOMALY';
    }

    return null;
  }

  public static setAttributes(args: Location | LegacyDevice): void {
    if (!args.attributes) return null;
    if (args.attributes && typeof args.attributes === 'string') {
      const json = new AttributeContainer(JSON.parse(args.attributes));
      args.normalizeAttributes = json
        ? (json?.attributes?.map(x => new Attribute(x)).filter(a => a.value && a.value !== '') as Attribute[])
        : null;
      // The argument is conditional because if the device comes from the backend, device.attributes contains a JSON with both attributes and coyoteWS
      // but if the device comes from the storageService, it has already been parsed and the two values (attributes and coyoteWS) are separated.
      // In this case, we get a device object containing a coyoteWS key and an already parsed attributes key.
      args.coyoteWS = json?.coyoteWS as CoyoteWS;
    }
  }

  private _groupByCoordinates(beacons: FullDevice[]): Beacon[] {
    if (!beacons.length) return [];

    const groups = new Map<string, { uids: number[]; count: number }>();

    for (const beacon of beacons) {
      const key =
        beacon.longitude != null && beacon.latitude != null
          ? `${beacon.longitude.toFixed(6)},${beacon.latitude.toFixed(6)}`
          : `uid_${beacon.uid}`;

      const existing = groups.get(key);
      if (existing) {
        existing.uids.push(beacon.uid);
        existing.count++;
      } else {
        groups.set(key, { uids: [beacon.uid], count: 1 });
      }
    }

    return beacons.map(beacon => {
      const key =
        beacon.longitude != null && beacon.latitude != null
          ? `${beacon.longitude.toFixed(6)},${beacon.latitude.toFixed(6)}`
          : `uid_${beacon.uid}`;

      const group = groups.get(key)!;
      return {
        ...beacon,
        count_beacons: group.count,
        ids_beacons: group.uids,
      } as Beacon;
    });
  }

  public calculateCSQ(csq?: number): string {
    if (csq == null || csq === 0 || csq === 99 || csq < 0 || csq > 31) {
      return 'unknown';
    }
    return `${Math.round((csq / 31) * 100)}%`;
  }

  public getMainDevices(devices: Location[], options?: { setAdditionalDevices?: boolean }) {
    const mains: Location[] = [];
    const groupedDevices = new Map<number, Location[]>();
    const additionalDevices = new Map<number, Location[]>();

    // 1. Regroup devices by fk_asset
    for (const device of devices) {
      if (!device.fk_asset) {
        mains.push(device);
        continue;
      }

      if (!groupedDevices.has(device.fk_asset)) {
        groupedDevices.set(device.fk_asset, []);
      }

      groupedDevices.get(device.fk_asset).push(device);
    }

    // 2. For each group, determine the master device and additional devices
    for (const [fk_asset, group] of groupedDevices) {
      const sortedDevices = group.sort((a, b) => b.weight - a.weight); // Sort by weight descending

      mains.push(sortedDevices[0]);

      if (sortedDevices.length > 1) {
        additionalDevices.set(fk_asset, sortedDevices.slice(1));
      }
    }

    if (options?.setAdditionalDevices) this.$additionalDevices.set(additionalDevices);

    return mains;
  }

  public getMainDevicesFromSocket(devices: Record<number, SocketLocation>) {
    const mainDevices: Record<number, SocketLocation> = {};

    for (const device of Object.values(devices)) {
      if (this.$devicesMap()?.[device.fk_device]) {
        mainDevices[device.fk_device] = this.$devicesMap()[device.fk_device];
      }
    }

    return mainDevices;
  }

  public getDeviceByFkAsset(fk_asset: number) {
    if (!fk_asset) return null;
    return this.$devices().find(device => device.fk_asset === fk_asset);
  }

  public setImmobileDevicesCount(serviceId: number, daysBefore: number): ImmobileDevice[] {
    const devices: FullDevice[] =
      serviceId === 0
        ? this.$devices().filter(device => !device.hasBeacon)
        : this.getFlattenedDevices(serviceId);

    const immobilityDevices = devices
      .map(device => this.findDeviceImmobile(device, daysBefore))
      .filter((device): device is ImmobileDevice => device !== null)
      .sort((a, b) => b.timeOfInactivity - a.timeOfInactivity);

    return immobilityDevices;
  }

  public getFlattenedDevices(serviceId: number) {
    return this._unitsService.getDevices(serviceId).filter(device => !device.hasBeacon) as FullDevice[];
  }

  private findDeviceImmobile(device: FullDevice, daysBefore: number): ImmobileDevice | null {
    if (device?.last_trip_stop === null || device?.last_trip_start === 0) {
      return null;
    }
    const now = new DateHelper();
    const today = new DateHelper().startOfDay().timestamp();
    const lastStop = new DateHelper(device?.last_trip_stop);
    const sinceStop = Math.round((now.getTime() - lastStop.getTime()) / (86400 * 1000));
    const noTripToday = device?.first_day_trip <= today;
    const isImmobile =
      (sinceStop > Number(daysBefore) && noTripToday) ||
      (sinceStop === 1 && Number(daysBefore) === 1 && lastStop.getDate() < now.getDate() && noTripToday);
    if (!isImmobile) {
      return null;
    }
    // Clone (prototype included) instead of mutating the shared store device, so its
    // timeOfInactivity isn't re-stamped (and drifting) on every call.
    const immobileDevice = Object.create(Object.getPrototypeOf(device)) as ImmobileDevice;
    return Object.assign(immobileDevice, device, {
      timeOfInactivity: now.getTime() - lastStop.getTime(),
    });
  }
}
